import React, { useEffect, useState } from 'react';
import {
  X, Save, Sparkles, Megaphone, ArrowLeftRight, Loader2,
} from 'lucide-react';
import { dailyPlanApi } from '../../api/services';
import type { DailyPlan } from '../../types/models';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ---------------------------------------------------------------------------
// DailyBriefingDialog — one dialog, three clearly-labelled surfaces:
//
//   1. AI briefing (read-only) — what Start My Day generated.
//   2. Executive briefing (editable) — EA's narrative for the exec, or the
//      exec's own framing. MCP clients can write this via update_plan_notes too.
//   3. Handoff notes (editable) — EA-to-cover-EA handover. Separate from the
//      exec briefing because the audience is different.
//
// Each editable field has its own save button to avoid accidentally publishing
// changes to a field the user didn't touch.
// ---------------------------------------------------------------------------

interface DailyBriefingDialogProps {
  open: boolean;
  onClose: () => void;
  date: string;
  plan: DailyPlan | null;
  forExecId?: string;
  onUpdated?: (updated: DailyPlan) => void;
}

export default function DailyBriefingDialog({
  open,
  onClose,
  date,
  plan,
  forExecId,
  onUpdated,
}: DailyBriefingDialogProps) {
  const [executiveBriefing, setExecutiveBriefing] = useState('');
  const [handoffNotes, setHandoffNotes] = useState('');
  const [saving, setSaving] = useState<'exec' | 'handoff' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setExecutiveBriefing(plan?.executiveBriefing ?? '');
    setHandoffNotes(plan?.handoffNotes ?? '');
    setError(null);
    setSavedMessage(null);
  }, [open, plan?.executiveBriefing, plan?.handoffNotes]);

  const hasPlan = Boolean(plan);
  const canSaveExec = hasPlan && executiveBriefing !== (plan?.executiveBriefing ?? '');
  const canSaveHandoff = hasPlan && handoffNotes !== (plan?.handoffNotes ?? '');

  const handleSaveExec = async () => {
    if (!plan) return;
    setSaving('exec');
    setError(null);
    try {
      const updated = await dailyPlanApi.updateExecutiveBriefing(date, executiveBriefing, forExecId);
      setSavedMessage('Executive briefing saved.');
      onUpdated?.(updated);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save the executive briefing.');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveHandoff = async () => {
    if (!plan) return;
    setSaving('handoff');
    setError(null);
    try {
      const updated = await dailyPlanApi.updateHandoffNotes(date, handoffNotes, forExecId);
      setSavedMessage('Handoff notes saved.');
      onUpdated?.(updated);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save the handoff notes.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && saving === null) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <DialogTitle>Daily briefing</DialogTitle>
              {plan?.dailyTheme && (
                <p className="mt-1 text-sm text-muted-foreground">{plan.dailyTheme}</p>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5"
              onClick={onClose}
              disabled={saving !== null}
            >
              <X className="h-4 w-4" />
              Close
            </Button>
          </div>
        </DialogHeader>

        <div className="border-y border-border py-4">
          {!plan && (
            <Alert>
              <AlertDescription>
                No plan has been generated yet. Run "Start my day" to produce a briefing, or write notes after the plan exists.
              </AlertDescription>
            </Alert>
          )}

          {plan && (
            <div className="flex flex-col gap-4">
              {plan.aiBriefing && (
                <div>
                  <div className="mb-1 flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <p className="text-sm font-semibold">AI briefing</p>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                    {plan.aiBriefing}
                  </p>
                </div>
              )}

              {plan.aiRisks && (
                <div>
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">Risks</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{plan.aiRisks}</p>
                </div>
              )}

              {(plan.aiBriefing || plan.aiRisks) && <div className="border-t border-border" />}

              <div>
                <div className="mb-1 flex items-center gap-1.5">
                  <Megaphone className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Executive briefing</p>
                  {plan.preparedBy && (
                    <span className="text-xs text-muted-foreground">Prepared by {plan.preparedBy}</span>
                  )}
                </div>
                <Textarea
                  rows={4}
                  value={executiveBriefing}
                  onChange={(e) => setExecutiveBriefing(e.target.value)}
                  placeholder="Narrative briefing written for the executive. MCP clients can update this too."
                  disabled={saving !== null}
                />
                <div className="mt-1.5 flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleSaveExec}
                    disabled={!canSaveExec || saving !== null}
                    className="gap-1.5"
                  >
                    {saving === 'exec' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {saving === 'exec' ? 'Saving…' : 'Save briefing'}
                  </Button>
                </div>
              </div>

              <div className="border-t border-border" />

              <div>
                <div className="mb-1 flex items-center gap-1.5">
                  <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold">Handoff notes</p>
                  <span className="text-xs text-muted-foreground">For a cover EA or the next shift</span>
                </div>
                <Textarea
                  rows={3}
                  value={handoffNotes}
                  onChange={(e) => setHandoffNotes(e.target.value)}
                  placeholder="Context for a cover EA: watch-outs, pending actions, people still chasing."
                  disabled={saving !== null}
                />
                <div className="mt-1.5 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveHandoff}
                    disabled={!canSaveHandoff || saving !== null}
                    className="gap-1.5"
                  >
                    {saving === 'handoff' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {saving === 'handoff' ? 'Saving…' : 'Save handoff'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <Alert variant="error" className="mt-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {savedMessage && !error && (
            <Alert variant="success" className="mt-3">
              <AlertDescription>{savedMessage}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving !== null}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
